import React, { useState, useEffect } from "react";
import { Box, Grid2 } from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../services/authentication/authContext";
import PieCharts from "./PieCharts";
import { dashboardService } from "../services/dashboardService";
import JobGroupPieChart from "./JobGroupPieChart ";
import MonthlyBreakBarChart from "./MonthlyBreakBarChart";
import { CHART_TITLES } from "../constants/chartTitles";
import JobsByEndDateChart from "./JobsByEndDateChart";
import BillableHoursPieChart from "./BillableHoursPieChart";
import MonthlyTargetHoursChart from "./MonthlyTargetHoursChart";
import PageHeader from "../component/ui/PageHeader";
import WidgetCard from "./widgets/WidgetCard";

const Dashboard = ({ userId }) => {
    const [roles, setRoles] = useState([]);
    const { accessToken } = useAuth();
    const isLeaderOrAdmin = Array.isArray(roles) && (roles.includes('ADMIN') || roles.includes('SUB_ADMIN') || roles.includes('MANAGE_JOBS'));
    const isSubAdmin = Array.isArray(roles) && roles.includes('SUB_ADMIN');
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [loadingTasks, setLoadingTasks] = useState(true);
    const [jobStatusChartData, setJobStatusChartData] = useState([]);
    const [liveLoginChartData, setLiveLoginChartData] = useState([]);

    const COLORS = ["#42a5f5", "#ffa726", "#66bb6a", "#F5D327"];

    const STATUS_COLORS = {
        "In progress": "#42a5f5",
        "Query": "#ffa726",
        "Review": "#66bb6a",
    };

    const TASK_TYPE_COLORS = {
        "Job Tasks": "#42a5f5",
        "Extra Tasks": "#ffa000",
        "Free Tasks": "#66bb6a",
        "Not Logged In": "#F5D327"
    };

    useEffect(() => {
        if (accessToken) {
            const decodedToken = jwtDecode(accessToken);
            let extractedRoles = [];

            if (Array.isArray(decodedToken.roles)) {
                extractedRoles = decodedToken.roles;
            } else if (typeof decodedToken.roles === "string") {
                extractedRoles = decodedToken.roles.split(",").map(role => role.trim());
            }

            setRoles(extractedRoles);
        }
    }, [accessToken]);

    const fetchJobStatusCounts = async () => {
        try {
            setLoadingStatus(true);
            const data = await dashboardService.getJobStatusCountsForDashboard();

            const chartFormatted = Object.entries(data)
                .filter(([_, value]) => value > 0)
                .map(([label, value], idx) => ({
                    id: idx,
                    value,
                    label,
                    color: STATUS_COLORS[label] || COLORS[idx % COLORS.length],
                }));

            setJobStatusChartData(chartFormatted);
        } catch (err) {
            console.error("Failed to fetch job end date counts", err);
        } finally {
            setLoadingStatus(false);
        }
    };

    const fetchActiveTasksSummaryCounts = async () => {
        try {
            setLoadingTasks(true);
            const data = await dashboardService.getActiveTasksSummaryForDashboard();

            const LABEL_MAP = {
                jobTasks: "Job Tasks",
                extraTasks: "Extra Tasks",
                freeTasks: "Free Tasks",
                inactiveUsers: "Not Logged In",
            };

            const chartFormatted = Object.entries(data)
                .filter(([_, value]) => value > 0)
                .map(([label, value], idx) => ({
                    id: idx,
                    value,
                    label: LABEL_MAP[label] || label,
                    color: TASK_TYPE_COLORS[LABEL_MAP[label]] || COLORS[idx % COLORS.length],
                }));

            setLiveLoginChartData(chartFormatted);
        } catch (err) {
            console.error("Failed to fetch job end date counts", err);
        } finally {
            setLoadingTasks(false);
        }
    };

    useEffect(() => {
        if (!accessToken || !userId) return;
        fetchJobStatusCounts();
        fetchActiveTasksSummaryCounts();
    }, [accessToken, userId])

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 2 } }}>
            <PageHeader
                title="Dashboard"
            />

            <Grid2 container spacing={2.25} alignItems="stretch">
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <WidgetCard
                        title={CHART_TITLES.JOBS_BY_STATUS}
                        subtitle="Current workload distribution"
                        badge={loadingStatus ? "Loading" : "Live"}
                        minHeight={340}
                    >
                        <PieCharts chartData={jobStatusChartData} title={CHART_TITLES.JOBS_BY_STATUS} loading={loadingStatus} />
                    </WidgetCard>
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                    <WidgetCard
                        title="Jobs to Go Today/Week"
                        subtitle="Upcoming delivery pressure"
                        badge="Timeline"
                        minHeight={340}
                    >
                        <JobsByEndDateChart />
                    </WidgetCard>
                </Grid2>

                {!isSubAdmin ? (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <WidgetCard
                            title="Efficient Billable Hours"
                            subtitle="Efficiency & utilization"
                            badge="Finance"
                            minHeight={340}
                        >
                            <BillableHoursPieChart roles={roles} currentUserID={userId} />
                        </WidgetCard>
                    </Grid2>
                ) : null}

                <Grid2 size={{ xs: 12, md: 6 }}>
                    <WidgetCard
                        title="Monthly breaks"
                        subtitle="Time off trend"
                        badge="Trend"
                        minHeight={340}
                    >
                        <MonthlyBreakBarChart roles={roles} currentUserID={userId} />
                    </WidgetCard>
                </Grid2>

                {isLeaderOrAdmin ? (
                    <>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <WidgetCard
                                title="Job Allocation Insights"
                                subtitle="Portfolio mix"
                                badge="Segments"
                                minHeight={340}
                            >
                                <JobGroupPieChart />
                            </WidgetCard>
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <WidgetCard
                                title={CHART_TITLES.LIVE_LOGIN}
                                subtitle="Active tasks summary"
                                badge={loadingTasks ? "Loading" : "Live"}
                                minHeight={340}
                            >
                                <PieCharts chartData={liveLoginChartData} title={CHART_TITLES.LIVE_LOGIN} loading={loadingTasks} />
                            </WidgetCard>
                        </Grid2>

                        {!isSubAdmin ? (
                            <Grid2 size={{ xs: 12 }}>
                                <WidgetCard
                                    title="Monthly target hours"
                                    subtitle="Target achievement by user"
                                    badge="Performance"
                                    minHeight={420}
                                >
                                    <MonthlyTargetHoursChart roles={roles} />
                                </WidgetCard>
                            </Grid2>
                        ) : null}
                    </>
                ) : null}
            </Grid2>

        </Box>
    );
};

export default Dashboard;